import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { BookOpen, Copy, Check, Home, Code, Send, Sparkles, AlertCircle, TrendingUp } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import PageHeader from './PageHeader';
import { API_BASE_URL } from '../config/api';

const BACKEND_URL = API_BASE_URL;

const AIPrompt = () => {
  const navigate = useNavigate();
  const [userInput, setUserInput] = useState('');
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [usageStatus, setUsageStatus] = useState(null);
  const [userId, setUserId] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [savedPrompts, setSavedPrompts] = useState([]);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [showPromptModal, setShowPromptModal] = useState(false);
  const [copiedPromptId, setCopiedPromptId] = useState(null);
  const messagesEndRef = useRef(null);
  
  // Initialize user session
  useEffect(() => {
    // Generate or retrieve user ID (using IP hash or session storage)
    let storedUserId = localStorage.getItem('tweeporter_user_id');
    if (!storedUserId) {
      storedUserId = 'user_' + Math.random().toString(36).substring(2, 15);
      localStorage.setItem('tweeporter_user_id', storedUserId);
    }
    setUserId(storedUserId);
    
    // Generate session ID
    const newSessionId = 'session_' + Date.now();
    setSessionId(newSessionId);
    
    // Load saved prompts from localStorage
    const saved = localStorage.getItem('tweeporter_saved_prompts');
    if (saved) {
      try {
        setSavedPrompts(JSON.parse(saved));
      } catch (e) {
        console.error('Failed to parse saved prompts:', e);
      }
    }
    
    // Fetch initial usage status
    fetchUsageStatus(storedUserId);
  }, []);
  
  const fetchUsageStatus = async (uid) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/usage-status?user_id=${uid}`);
      if (response.ok) {
        const data = await response.json();
        setUsageStatus(data);
      }
    } catch (error) {
      console.error('Failed to fetch usage status:', error);
    }
  };
  
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  
  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  
  const handleSendMessage = async () => {
    if (!userInput.trim() || isLoading) return;
    
    const userMessage = userInput.trim();
    setUserInput('');
    
    // Add user message to chat
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/generate-story`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: userMessage,
          user_id: userId,
          session_id: sessionId
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to generate story');
      }
      
      const data = await response.json();
      
      // Add AI response to chat
      setMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
      
      // Save prompt to localStorage
      const newPrompt = {
        id: Date.now().toString(),
        query: userMessage,
        response: data.response,
        timestamp: new Date().toISOString()
      };
      
      const updatedPrompts = [newPrompt, ...savedPrompts].slice(0, 20); // Keep only last 20
      setSavedPrompts(updatedPrompts);
      localStorage.setItem('tweeporter_saved_prompts', JSON.stringify(updatedPrompts));
      
      // Update usage status
      setUsageStatus({
        user_prompts_used: 5 - data.remaining_prompts,
        user_prompts_remaining: data.remaining_prompts,
        usage_percentage: data.daily_usage_percentage,
        status: data.status,
        max_prompts_per_user: 5
      });
      
      toast({
        title: 'Story generated!',
        description: `${data.remaining_prompts} prompts remaining today`
      });
      
    } catch (error) {
      console.error('Error:', error);
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
      
      // Remove user message if request failed
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleCopyTwee = async (content) => {
    try {
      await navigator.clipboard.writeText(content);
      toast({
        title: 'Copied!',
        description: 'Twee code copied to clipboard'
      });
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };
  
  const handleSendToEditor = (content) => {
    // Store the Twee content in sessionStorage
    sessionStorage.setItem('imported_twee', content);
    // Navigate to visual editor
    navigate('/visual-editor');
  };
  
  const handleOpenPromptModal = (prompt) => {
    setSelectedPrompt(prompt);
    setShowPromptModal(true);
  };
  
  const handleCopyPrompt = async (promptId, content) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedPromptId(promptId);
      toast({
        title: 'Copied!',
        description: 'Twee code copied to clipboard'
      });
      setTimeout(() => setCopiedPromptId(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      toast({
        title: 'Error',
        description: 'Failed to copy to clipboard',
        variant: 'destructive'
      });
    }
  };
  
  const getStatusColor = () => {
    if (!usageStatus) return 'bg-gray-200';
    
    if (usageStatus.status === 'red') return 'bg-red-500';
    if (usageStatus.status === 'orange') return 'bg-orange-500';
    return 'bg-green-500';
  };
  
  const getStatusText = () => {
    if (!usageStatus) return 'Loading...';
    
    if (usageStatus.status === 'red') return 'Daily limit reached';
    if (usageStatus.status === 'orange') return 'High usage (70%+)';
    return 'Available';
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <PageHeader borderBottom={true} />

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        <div className="max-w-5xl mx-auto">
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-slate-900 mb-2 flex items-center gap-2">
              <Sparkles className="h-8 w-8 text-indigo-600" />
              AI Story Generator
            </h1>
            <p className="text-slate-600">
              Describe your interactive story idea, and our AI will generate properly formatted SugarCube Twee code for you.
            </p>
          </div>
          
          {/* Usage Status Bar */}
          {usageStatus && (
            <div className="mb-6 bg-white rounded-lg shadow-sm border border-slate-200 p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-slate-600" />
                  <span className="font-semibold text-slate-900">Usage Status</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded-full ${getStatusColor()}`}></div>
                  <span className="text-sm text-slate-600">{getStatusText()}</span>
                </div>
              </div>
              
              <div className="space-y-2">
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-slate-600">Your prompts today:</span>
                    <span className="font-medium">
                      {usageStatus.user_prompts_used} / {usageStatus.max_prompts_per_user}
                    </span>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-2">
                    <div 
                      className="bg-indigo-600 h-2 rounded-full transition-all"
                      style={{ width: `${(usageStatus.user_prompts_used / usageStatus.max_prompts_per_user) * 100}%` }}
                    ></div>
                  </div>
                </div>
                
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-slate-600">Daily capacity:</span>
                    <span className="font-medium">{usageStatus.usage_percentage}%</span>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full transition-all ${
                        usageStatus.status === 'red' ? 'bg-red-500' :
                        usageStatus.status === 'orange' ? 'bg-orange-500' : 'bg-green-500'
                      }`}
                      style={{ width: `${Math.min(usageStatus.usage_percentage, 100)}%` }}
                    ></div>
                  </div>
                </div>
              </div>
              
              {usageStatus.user_prompts_remaining === 0 && (
                <div className="mt-3 flex items-start gap-2 text-sm text-amber-700 bg-amber-50 p-2 rounded">
                  <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span>You've reached your daily limit of 5 prompts. Please try again tomorrow.</span>
                </div>
              )}
            </div>
          )}
          
          {/* Previously Generated Prompts */}
          {savedPrompts.length > 0 && (
            <div className="mb-6 bg-white rounded-lg shadow-sm border border-slate-200 p-4">
              <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <BookOpen className="h-5 w-5 text-indigo-600" />
                Previously Generated Prompts
              </h3>
              <div className="space-y-2">
                {savedPrompts.slice(0, 5).map((prompt) => (
                  <button
                    key={prompt.id}
                    onClick={() => handleOpenPromptModal(prompt)}
                    className="w-full text-left p-3 rounded-lg border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">
                          {prompt.query}
                        </p>
                        <p className="text-xs text-slate-500 mt-1">
                          {new Date(prompt.timestamp).toLocaleDateString()} at{' '}
                          {new Date(prompt.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                      <Code className="h-4 w-4 text-indigo-600 ml-2 flex-shrink-0" />
                    </div>
                  </button>
                ))}
              </div>
              {savedPrompts.length > 5 && (
                <p className="text-xs text-slate-500 mt-3 text-center">
                  Showing 5 of {savedPrompts.length} saved prompts
                </p>
              )}
            </div>
          )}
          
          {/* Chat Interface */}
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
            {/* Messages Area */}
            <div className="h-[500px] overflow-y-auto p-4 space-y-4">
              {messages.length === 0 && (
                <div className="h-full flex flex-col items-center justify-center text-center p-8">
                  <Sparkles className="h-16 w-16 text-indigo-200 mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">Start Your Story</h3>
                  <p className="text-slate-600 max-w-md">
                    Describe your interactive story idea below. For example:
                  </p>
                  <div className="mt-4 text-sm text-slate-500 space-y-1 max-w-lg">
                    <p>• "Create a customer onboarding story for a SaaS product"</p>
                    <p>• "Build a training scenario for cybersecurity awareness"</p>
                    <p>• "Generate a sales decision tree for enterprise software"</p>
                  </div>
                </div>
              )}
              
              {messages.map((message, index) => (
                <div
                  key={index}
                  className={`flex ${
                    message.role === 'user' ? 'justify-end' : 'justify-start'
                  }`}
                >
                  <div
                    className={`max-w-[80%] rounded-lg p-4 ${
                      message.role === 'user'
                        ? 'bg-indigo-600 text-white'
                        : 'bg-slate-100 text-slate-900'
                    }`}
                  >
                    {message.role === 'assistant' ? (
                      <div>
                        <pre className="whitespace-pre-wrap font-mono text-sm mb-3 overflow-x-auto">
                          {message.content}
                        </pre>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleCopyTwee(message.content)}
                            className="text-xs"
                          >
                            <Copy className="h-3 w-3 mr-1" />
                            Copy Twee
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => handleSendToEditor(message.content)}
                            className="text-xs bg-indigo-600 hover:bg-indigo-700 text-white"
                          >
                            <Code className="h-3 w-3 mr-1" />
                            Open in Editor
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm">{message.content}</p>
                    )}
                  </div>
                </div>
              ))}
              
              {isLoading && (
                <div className="flex justify-start">
                  <div className="bg-slate-100 rounded-lg p-4">
                    <div className="flex items-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600"></div>
                      <span className="text-sm text-slate-600">Generating story...</span>
                    </div>
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>
            
            {/* Input Area */}
            <div className="border-t border-slate-200 p-4">
              <div className="flex gap-2">
                <Textarea
                  value={userInput}
                  onChange={(e) => setUserInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                  placeholder="Describe your story idea... (Press Enter to send, Shift+Enter for new line)"
                  className="resize-none"
                  rows={3}
                  disabled={isLoading || (usageStatus && usageStatus.user_prompts_remaining === 0)}
                />
                <Button
                  onClick={handleSendMessage}
                  disabled={!userInput.trim() || isLoading || (usageStatus && usageStatus.user_prompts_remaining === 0)}
                  className="bg-indigo-600 hover:bg-indigo-700"
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-xs text-slate-500 mt-2">
                {usageStatus && `${usageStatus.user_prompts_remaining} prompt${usageStatus.user_prompts_remaining !== 1 ? 's' : ''} remaining today`}
              </p>
            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="mt-8 flex items-center justify-center space-x-4">
            <Button
              size="lg"
              variant="outline"
              onClick={() => navigate('/')}
            >
              <Home className="h-5 w-5 mr-2" />
              Back to Home
            </Button>
            <Button
              size="lg"
              onClick={() => navigate('/visual-editor')}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              <Code className="h-5 w-5 mr-2" />
              Go to Visual Editor
            </Button>
          </div>
        </div>
      </main>
      
      {/* Prompt Modal */}
      {showPromptModal && selectedPrompt && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowPromptModal(false)}
        >
          <div 
            className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[80vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="bg-indigo-600 text-white p-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Code className="h-5 w-5" />
                Generated Story
              </h3>
              <button
                onClick={() => setShowPromptModal(false)}
                className="text-white hover:bg-indigo-700 rounded p-1 transition-colors"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {/* Modal Body */}
            <div className="p-6 overflow-y-auto max-h-[calc(80vh-180px)]">
              {/* Original Query */}
              <div className="mb-4">
                <h4 className="text-sm font-semibold text-slate-700 mb-2">Your Request:</h4>
                <p className="text-sm text-slate-600 bg-slate-50 p-3 rounded border border-slate-200">
                  {selectedPrompt.query}
                </p>
              </div>
              
              {/* Generated Code */}
              <div>
                <h4 className="text-sm font-semibold text-slate-700 mb-2">Generated Twee Code:</h4>
                <div className="bg-slate-900 text-slate-100 p-4 rounded font-mono text-sm overflow-x-auto">
                  <pre className="whitespace-pre-wrap break-words">{selectedPrompt.response}</pre>
                </div>
              </div>
              
              {/* Timestamp */}
              <p className="text-xs text-slate-500 mt-3">
                Generated on {new Date(selectedPrompt.timestamp).toLocaleString()}
              </p>
            </div>
            
            {/* Modal Footer */}
            <div className="bg-slate-50 p-4 flex items-center justify-end gap-3 border-t border-slate-200">
              <Button
                variant="outline"
                onClick={() => setShowPromptModal(false)}
              >
                Close
              </Button>
              <Button
                onClick={() => handleCopyPrompt(selectedPrompt.id, selectedPrompt.response)}
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                {copiedPromptId === selectedPrompt.id ? (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4 mr-2" />
                    Copy to Clipboard
                  </>
                )}
              </Button>
              <Button
                onClick={() => {
                  handleSendToEditor(selectedPrompt.response);
                  setShowPromptModal(false);
                }}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                <Code className="h-4 w-4 mr-2" />
                Send to Editor
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIPrompt;
